import { forwardRef, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cx } from './cx';

// Select moved to its own file; re-exported here so existing imports keep working.
export { Select } from './Select';
export type { SelectOption, SelectProps } from './Select';

const CONTROL =
  'w-full rounded-control border border-hairline-strong bg-input px-3 text-body text-ink placeholder:text-ink-faint focus:border-accent focus:bg-surface-lowest focus:outline-none disabled:opacity-60 transition-colors';

export function Field({ label, hint, error, children, className, htmlFor }: { label?: ReactNode; hint?: ReactNode; error?: ReactNode; children: ReactNode; className?: string; htmlFor?: string }) {
  return (
    <div className={cx('space-y-1.5', className)}>
      {label ? (
        <label htmlFor={htmlFor} className="block text-caption font-semibold text-ink-strong">
          {label}
        </label>
      ) : null}
      {children}
      {error ? <p className="text-caption text-error">{error}</p> : hint ? <p className="text-caption text-ink-faint">{hint}</p> : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cx(CONTROL, 'h-9', className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx(CONTROL, 'py-2.5 min-h-[96px] resize-y', className)} {...rest} />;
});

export function Checkbox({ label, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={cx('inline-flex items-center gap-2.5 text-body-sm text-ink cursor-pointer', className)}>
      <input type="checkbox" className="h-4.5 w-4.5 rounded-xs border-hairline-strong accent-[var(--primary)]" {...rest} />
      {label}
    </label>
  );
}
